import Axios, { AxiosRequestConfig, CancelToken } from 'axios';
import {
  BackendSettings,
  BackendV2Api,
  ContentApi,
  LicenseEnum,
  PicsartApi,
  ProcessingTask,
  ProcessingTaskStatus,
  StockVideosApi,
  TextTemplateItem
} from 'api';
import { API_URL, TEMPLATE_LIST_URL, TRANSITIONS_URL, UPLOAD_URL } from './env';
import { authService } from '@picsart/rc/services/auth';
import { TemplateListItem } from 'types/templateTypes';
import { AppState, PaidFeatures } from 'types/AppState';
import { eventChannel } from 'redux-saga';
import { emitAxiosError } from 'modules/alert';
import { StockVideo } from 'types/StockVideo';
import { NewSong } from './audio/audio';
import { Licenses } from 'types/Licenses';
import { PremiumItemSource } from '../constants';
import { Scene } from 'types/Scene';
import { UserModel } from '@picsart/rc/types/models/user';
import { authorizationHeader } from '@picsart/growth-rc/services/auth';

const processingApi = new BackendV2Api({ basePath: API_URL });
const contentApi = new ContentApi({ basePath: UPLOAD_URL });
const picsartApi = new PicsartApi({ basePath: API_URL });
// const picsartLiveApi = new PicsartLiveApi({ basePath: REACT_APP_API_URL }); // Shutterstock doesn't fully work on stage
const picsartStockVideosApi = new StockVideosApi({ basePath: API_URL });

const getAuthOptions = (key: string) => ({
  headers: {
    ...authorizationHeader(key)
  }
});

export const fetchUserData = async (): Promise<UserModel | null> => {
  try {
    const data = await authService.fetchUser({}, '/');

    if (data?.status !== 'error') {
      return data?.user ?? null;
    }

    return null;
  } catch (e) {
    // eslint-disable-next-line
    console.error('fetch user error: ', e);
    return null;
  }
};

export const getTaskById = async (
  taskId: string,
  userKey: string
): Promise<ProcessingTaskStatus> => {
  const res = await processingApi.getTaskByIdV3(taskId, getAuthOptions(userKey));
  return res.data;
};

export const createTask = async (data: ProcessingTask, userKey: string): Promise<string> => {
  const res = await processingApi.createTaskV3(data, getAuthOptions(userKey));
  return res.data.taskId;
};

export const deleteTask = async (taskId: string, userKey: string): Promise<number> => {
  const res = await processingApi.deleteTaskByIdV3(taskId, getAuthOptions(userKey));
  return res.status;
};

export const createPreprocessingTask = async (url: string, userKey: string): Promise<string> => {
  const res = await processingApi.createPreprocessingTaskV3(
    { content: { url } },
    getAuthOptions(userKey)
  );
  return res.data.taskId;
};

export const createStockvideoTaskV3 = async (videoId: string, userKey: string): Promise<string> => {
  const res = await processingApi.createStockvideoTaskV3(videoId, getAuthOptions(userKey));
  return res.data.taskId;
};

export const addContent = async (
  file: File,
  userKey: string,
  handleProgress?: (progress: number) => void,
  cancelToken?: CancelToken
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const onUploadProgress: AxiosRequestConfig['onUploadProgress'] = e => {
    if (handleProgress) {
      const percent = Math.round((e.loaded * 100) / e.total);
      handleProgress(percent);
    }
  };

  const res = await contentApi.addContent('wve-input', file, {
    onUploadProgress,
    cancelToken,
    ...getAuthOptions(userKey)
  });
  return res.data.result.url;
};

export const listBackendSettings = async (userKey: string): Promise<BackendSettings> => {
  const res = await processingApi.getBackendSettingsV3(getAuthOptions(userKey));

  res.data.transitions.fadeDuration *= 1000;
  res.data.transitions.wipeDiagonalDuration *= 1000;

  return res.data;
};

export const getFileSize = async (
  url: string,
  format: (x: number) => number = num => Math.round(num / 2 ** 20)
): Promise<number> => {
  try {
    const res = await Axios.head(url);
    return format(res.headers['content-length']);
  } catch {
    const res = await Axios.get(url);
    return format(res.headers['content-length']);
  }
};

export const createAxiosInterceptorChannel = () => {
  return eventChannel(emit => {
    Axios.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        if (!error.response) {
          // Thus I want to filter out errors from Axios.Canceler, when we cancel loading of image
          if ('message' in error && error.message === undefined) return;

          // eslint-disable-next-line
          console.error(error);
          return Promise.reject(error);
        }

        emit(emitAxiosError(error.response?.config?.url, error.response.status));
      }
    );
    return () => null;
  });
};

// TODO: use TemplatesApi when it is done
export async function loadTemplatesList(): Promise<TemplateListItem[]> {
  const res = await Axios.get(TEMPLATE_LIST_URL);
  return res.data;
}

export async function loadTemplateData(url: string): Promise<Partial<AppState>> {
  if (!url) return;

  const res = await Axios.get(url);
  return res.data;
}

export const getTransitionsData = async () => {
  const res = await Axios.get(TRANSITIONS_URL);
  return res.data;
};

export const getShutterstockImageUrlById = async (userKey: string, id: string) => {
  // const res = await picsartLiveApi.getShutterstockImageUrlById(id, null, 'view', 'website');
  // return res.data.response?.data[0]?.url ?? null;
  // TODO: change schema of "picsartLiveApi.getShutterstockImageUrlById" according to new format
  const res = await Axios.get(`${API_URL}/shutterstock/photo/${id}/url`, {
    headers: {
      platform: 'website',
      ...authorizationHeader(userKey)
    }
  });
  return res.data.response.url ?? null;
};

export const getTextTemplate = async (templateId: string): Promise<TextTemplateItem> => {
  const res = await picsartApi.getTextTemplate(templateId);
  return res.data.response;
};

const calcFormat = (width: number, height: number): string => {
  if (width / 9 === height / 16) {
    return '16:9';
  }

  if (width / 4 === height / 5) {
    return '4:5';
  }

  if (width === height) {
    return '1:1';
  }

  return '16:9';
};

export const getStockVideos = async ({
  query,
  limit = 40,
  offset,
  commercial,
  licence
}: {
  query?: string;
  limit?: number;
  offset?: number;
  licence?: 'fte' | 'premium';
  commercial?: boolean;
}): Promise<StockVideo[]> => {
  const res = await picsartStockVideosApi.getStockVideosList(
    limit,
    offset,
    query,
    licence,
    !!commercial
  );

  return (
    res.data?.response?.map<StockVideo>(item => ({
      id: item._id,
      url: item.url,
      tags: item.tags,
      format: calcFormat(item.preview_small.width, item.preview_small.height),
      preview_url: item.preview_medium.url,
      thumbnail_url: item.thumbnail_url,
      midResSrc: item.preview_medium.url,
      license:
        item.license === LicenseEnum.Premium ? Licenses.StockVideoPremium : Licenses.StockVideoFree,
      isCommercial: item.commercial,
      source: PremiumItemSource.Picsart,
      bigResSrc: item?.preview_big?.url,
      previewSize: {
        height: item.preview_medium.height,
        width: item.preview_medium.width
      }
    })) || []
  );
};

// For now we don't use this, because CMS sends us empty categories,
// and we don't know how to tell them about it...
export const getAudioCategories = async (): Promise<
  { name: string; collection_name: string }[]
> => {
  const res = await Axios.get(`${API_URL}/audio/category`);
  return res.data?.response?.collections || [];
};

export const getAudios = async (): Promise<NewSong[]> => {
  const res = await Axios.get(`${API_URL}/audio/musics?page=1&limit=300`);
  return res.data?.response?.songs || [];
};

type SubscriptionPackages = {
  data: {
    id: string;
    price: {
      value: number;
      currency: string;
    };
    source: PremiumItemSource;
    type?: PaidFeatures;
  }[];
  success: string;
};
export const getSubscriptionPackages = async (): Promise<SubscriptionPackages | undefined> => {
  const res = await Axios.get(`${API_URL}/shop/subscription/items-packages`);
  return res.data;
};

type UserPaidItems = {
  data: any[];
  success: string;
};
export const getUserPaidItemsRequest = async (
  userKey: string
): Promise<UserPaidItems | undefined> => {
  const res = await Axios.get(`${API_URL}/shop/subscription/users/items`, {
    ...getAuthOptions(userKey)
  });

  return res.data ? res.data.data : [];
};

export const getShutterstockVideos = async (
  userKey: string,
  query: string
): Promise<StockVideo[]> => {
  const res = await Axios.get(
    `${API_URL}/search/create-flow/shutterstock-video/results?q=${encodeURIComponent(query)}`,
    {
      headers: {
        platform: 'website',
        ...authorizationHeader(userKey)
      }
    }
  );

  return (
    res.data?.response[0]?.data.map((item: any) => ({
      id: String(item.id),
      midResSrc: item.preview_url,
      license: Licenses.ShutterstockVideo,
      format: item.aspect_ratio,
      preview_url: item.preview_url,
      thumbnail_url: item.thumbnail_url,
      duration: item.duration,
      description: item.description,
      source: PremiumItemSource.Shutterstock,
      is_purchased_for_user: item.is_purchased_for_user
    })) || []
  );
};

export const getShutterstockVideoOriginalUrl = async (
  userKey: string,
  scene: Scene
): Promise<string> => {
  const res = await Axios.get(`${API_URL}/shutterstock/video/${scene.stockVideoDownloadId}/url`, {
    headers: {
      platform: 'website',
      ...authorizationHeader(userKey)
    }
  });
  return res.data?.response?.url ?? '';
};

export const chargeShutterstock = (userKey: string, items: any[]) => {
  Axios.post(
    `${API_URL}/shutterstock/items/charge`,
    {
      items
    },
    getAuthOptions(userKey)
  );
};
